import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Calling V2 → Agent Templates list.
 *
 * Admin-curated library of agent templates that customers can buy and clone.
 * Each row maps to one calling_v2.AgentTemplate. Pricing lives on the linked
 * AgentPlan (category='calling_v2'), upserted by the editor on save.
 */
export default function CallingV2Templates() {
  document.title = "Digital Employees — Calling V2 Agent Templates";
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await CallingV2.listTemplates();
      const list = resp?.data?.results || [];
      list.forEach((r, i) => { r.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load Calling V2 templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const toggleStatus = async (row) => {
    try {
      await CallingV2.updateTemplate(row.id, { is_active: !row.is_active });
      triggerAlert("success", "Success", "Status updated.");
      fetchRows();
    } catch (e) {
      triggerAlert("error", "Oops...", "Could not update status.");
    }
  };

  const deleteRow = async (row) => {
    const ok = await confirm({
      title: "Deactivate template?",
      message: `Deactivate template "${row.name}"? Existing customer agents stay active.`,
      confirmLabel: "Deactivate",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteTemplate(row.id);
      triggerAlert("success", "Deactivated", "Template hidden from marketplace.");
      fetchRows();
    } catch (e) {
      triggerAlert("error", "Oops...", "Delete failed.");
    }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    {
      name: "Name", sortable: true, selector: (r) => r.name,
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div style={{ fontSize: 12, color: "#6c757d", fontFamily: "monospace" }}>{r.slug}</div>
        </div>
      ),
    },
    {
      name: "Direction", sortable: true, selector: (r) => r.direction, width: "120px",
      cell: (r) => <span className="badge bg-info">{r.direction}</span>,
    },
    {
      name: "Mode", sortable: true, selector: (r) => r.mode, width: "120px",
      cell: (r) => <span className="badge bg-secondary">{r.mode}</span>,
    },
    {
      name: "Monthly Price", sortable: true, selector: (r) => Number(r.monthly_price || 0),
      cell: (r) => <span style={{ fontWeight: 600 }}>{r.monthly_price != null ? `$${Number(r.monthly_price).toFixed(2)}/mo` : "—"}</span>,
    },
    {
      name: "Marketplace", sortable: true, selector: (r) => r.is_marketplace,
      cell: (r) => r.is_marketplace
        ? <span className="badge bg-success">Listed</span>
        : <span className="badge bg-light text-dark">Draft</span>,
      width: "120px",
    },
    {
      name: "Status", sortable: true, selector: (r) => r.is_active, width: "110px",
      cell: (r) => r.is_active
        ? <span className="badge bg-success">Active</span>
        : <span className="badge bg-danger">Inactive</span>,
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => navigate(`/v2/templates/${r.id}`)}>Edit</button>
          <button className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`} onClick={() => toggleStatus(r)}>
            {r.is_active ? "Pause" : "Activate"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => deleteRow(r)}>Delete</button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "#064c77", borderBottom: "1px solid #dee2e6", minHeight: "52px" } },
    headCells: { style: { color: "#ffffff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "60px", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8f9fa" } } },
    cells: { style: { fontSize: "14px", color: "#495057", borderBottom: "1px solid #f1f3f5" } },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Agent Templates" heading="Calling V2 / Agent Templates" />
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="alert alert-info py-2 mb-3">
                      These templates are the catalog customers see on the marketplace. Each
                      template is cloned into a customer-owned Agent when they purchase its plan.
                    </div>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-8"></div>
                      <div className="col-md-4 text-end">
                        <button className="btn btn-primary w-md btn-md" onClick={() => navigate("/v2/templates/new")}>
                          + New Template
                        </button>
                      </div>
                    </div>
                    {!loading ? (
                      <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        fixedHeader
                        fixedHeaderScrollHeight="640px"
                        persistTableHead
                        customStyles={customStyles}
                        noDataComponent={<div style={{ padding: 24 }}>No templates yet. Click "+ New Template" to publish one.</div>}
                      />
                    ) : (
                      <div className="loader-overlay text-white"><Loader /></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
